/**
 * Actions and pages — the half of the palette that never touches the network.
 *
 * Matched synchronously on every keystroke. That is the whole reason this file
 * exists separately from `searchConsole`: the most predictable rows in the
 * palette must also be the fastest, and a "give credit" row that arrives 180ms
 * after the operator typed "giv" has already lost the race against their hands.
 *
 * Everything here ROUTES. Nothing mutates. A palette is a place you pass
 * through at speed with a virtual cursor you may have moved by accident, so an
 * ⏎ on the wrong row must never move money — it must land you on the screen that
 * asks you to confirm it.
 */

import { SEARCH_SCORE, compareHits, matchField, type SearchHit } from '@/data/api'
import { ROLE_LABEL, type Account, type Role } from '@/data/types'
import { navItemsForRole } from '@/app/nav'
import { canAccessLogPage } from '@/features/logs/access'
import type { IconName } from '@w7/ui'

/** What the palette knows about the signed-in operator when it ranks actions. */
export interface ActionContext {
  /** The signed-in account's role. Gates role-specific actions. */
  role?: Role
  /**
   * DIRECT children only. Directive 4 is absolute — give and take apply to
   * direct children and nothing else — so a deeper account produces NO give/take
   * row rather than a disabled one. A disabled row in a palette is unreachable
   * noise that still costs a ↓ press.
   */
  children?: Pick<Account, 'id' | 'username' | 'displayName' | 'role'>[]
}

export interface ActionDef {
  id: string
  /** The verb phrase. Weighted as a primary field, like a username. */
  label: string
  /** What it does — shown as the row's subtitle, matched at tertiary weight. */
  subtitle: string
  /**
   * The vocabulary of the product being replaced. An operator will type "net
   * exposure", "account statement", "view member" or "monday" — the keywords are
   * how the old words still find the new screen.
   */
  keywords: string[]
  icon: IconName
  to: string
  when?: (ctx: ActionContext) => boolean
}

/**
 * The static catalog.
 */
export const ACTIONS: ActionDef[] = [
  {
    id: 'action:my-pnl',
    label: 'My P/L',
    subtitle: 'Settled book P/L by Sub Game — Lab master-dashboard-new',
    keywords: ['profit', 'loss', 'earnings', 'my profit and loss', 'pl', 'my p/l'],
    icon: 'pnl',
    to: '/reports/my-pnl',
  },
  {
    id: 'action:overview',
    label: 'Overview',
    subtitle: 'Position, trend and anything needing attention',
    keywords: ['overview', 'home', 'dashboard', 'attention'],
    icon: 'overview',
    to: '/overview',
  },
  {
    id: 'action:aggregate-accounts',
    label: 'Aggregate Accounts',
    subtitle: 'Linked Aggregate group members — credit limit, Take, Give, and status',
    keywords: ['aggregate', 'accounts', 'members', 'agents', 'list'],
    icon: 'users',
    to: '/aggregate/accounts',
  },
  {
    id: 'action:hierarchy-search',
    label: 'Hierarchy Search',
    subtitle: 'Find anyone in your Aggregate group and see the upline chain',
    keywords: ['hierarchy', 'search', 'upline', 'chain', 'player', 'tm'],
    icon: 'search',
    to: '/hierarchy-search',
  },
  {
    id: 'action:logs-cl-transfers',
    label: 'CL Transfers',
    subtitle: 'Credit-limit changes across the hierarchy',
    keywords: ['cl transfers', 'credit limit logs', 'all logs', 'logs'],
    icon: 'sliders',
    to: '/logs/cl-transfers',
    when: (ctx) => canAccessLogPage(ctx.role, 'cl-transfers'),
  },
  {
    id: 'action:logs-multiple-device-ids',
    label: 'Multiple Device Ids',
    subtitle: 'Players sharing the same device id',
    keywords: ['multiple device', 'device ids', 'all logs', 'logs'],
    icon: 'users',
    to: '/logs/multiple-device-ids',
    when: (ctx) => canAccessLogPage(ctx.role, 'multiple-device-ids'),
  },
  {
    id: 'action:logs-betfair-rates',
    label: 'BetfairRates Logs',
    subtitle: 'Betfair rate and min-bet changes',
    keywords: ['betfair rates', 'betfair logs', 'all logs', 'logs'],
    icon: 'sport',
    to: '/logs/betfair-rates',
    when: (ctx) => canAccessLogPage(ctx.role, 'betfair-rates'),
  },
  {
    id: 'action:logs-invalid-bets',
    label: 'Invalid Bets',
    subtitle: 'VAR and invalid bet logs',
    keywords: ['invalid bets', 'var bets', 'all logs', 'logs'],
    icon: 'warning',
    to: '/logs/invalid-bets',
    when: (ctx) => canAccessLogPage(ctx.role, 'invalid-bets'),
  },
  {
    id: 'action:logs-blocked-device-ids',
    label: 'Blocked Device Ids',
    subtitle: 'Blocked device ids and linked players',
    keywords: ['blocked device', 'device block', 'all logs', 'logs'],
    icon: 'lock',
    to: '/logs/blocked-device-ids',
    when: (ctx) => canAccessLogPage(ctx.role, 'blocked-device-ids'),
  },
  {
    id: 'action:logs-blocked-ip',
    label: 'Blocked IP',
    subtitle: 'Blocked IPs, comments, and players',
    keywords: ['blocked ip', 'ip block', 'all logs', 'logs'],
    icon: 'lock',
    to: '/logs/blocked-ip',
    when: (ctx) => canAccessLogPage(ctx.role, 'blocked-ip'),
  },
  {
    id: 'action:logs-login-from-blocked-ip',
    label: 'Login From Blocked IP',
    subtitle: 'Sign-ins that came from a blocked IP',
    keywords: ['login blocked ip', 'blocked login', 'all logs', 'logs'],
    icon: 'shield',
    to: '/logs/login-from-blocked-ip',
    when: (ctx) => canAccessLogPage(ctx.role, 'login-from-blocked-ip'),
  },
]

/**
 * Score one action against the needle.
 *
 * Field weights mirror the data-layer ladder exactly: the verb phrase is a
 * primary field like a username, the old product's vocabulary is secondary, and
 * the explanatory subtitle is tertiary. Highlight ranges are emitted only for a
 * LABEL match — marking the label at offsets computed against a keyword would
 * paint the highlight in the wrong place, which is worse than none.
 */
function scoreAction(def: ActionDef, needle: string): { score: number; ranges: [number, number][] } | null {
  let best: { score: number; ranges: [number, number][] } | null = null

  const consider = (score: number, ranges: [number, number][]) => {
    if (!best || score > best.score) best = { score, ranges }
  }

  const label = matchField(def.label, needle)
  if (label) {
    consider(
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[label.quality] * SEARCH_SCORE.weight.primary,
      label.ranges,
    )
  }
  for (const keyword of def.keywords) {
    const hit = matchField(keyword, needle)
    if (!hit) continue
    consider(
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[hit.quality] * SEARCH_SCORE.weight.secondary,
      [],
    )
  }
  const subtitle = matchField(def.subtitle, needle)
  if (subtitle) {
    consider(
      SEARCH_SCORE.base.action +
        SEARCH_SCORE.quality[subtitle.quality] * SEARCH_SCORE.weight.tertiary,
      [],
    )
  }

  return best
}

/**
 * Quality good enough to conjure the templated give/take/statement/bets rows.
 *
 * A subsequence hit on a username must not: "abc" matches half the downline by
 * construction, and four action rows per accidental match would unbury the four
 * real answers underneath them.
 */
const TEMPLATE_QUALITIES = new Set(['exact', 'prefix', 'word-prefix'])

/**
 * The contextual rows, for the ONE direct child the operator most likely means.
 *
 * One child, not every match: four rows each across three near-miss usernames is
 * twelve rows of speculation above the account results that actually answer the
 * question. The best match by username quality is the one they typed at.
 */
function templatedActions(ctx: ActionContext, needle: string): SearchHit[] {
  const children = ctx.children ?? []
  let best: { child: Pick<Account, 'id' | 'username' | 'role'>; score: number; ranges: [number, number][] } | null =
    null

  for (const child of children) {
    const match = matchField(child.username, needle)
    if (!match || !TEMPLATE_QUALITIES.has(match.quality)) continue
    const score =
      SEARCH_SCORE.base.action + SEARCH_SCORE.quality[match.quality] * SEARCH_SCORE.weight.primary
    if (best && score <= best.score) continue
    best = { child, score, ranges: match.ranges }
  }

  if (!best) return []
  const { child, score, ranges } = best
  const who = child.username
  const tier = ROLE_LABEL[child.role]
  // Ranges are computed against the username, so they are mapped onto the
  // offset at which the username appears inside each row's title.
  const shift = (offset: number): [number, number][] =>
    ranges.map(([s, e]) => [s + offset, e + offset] as [number, number])

  const rows: { id: string; title: string; subtitle: string; icon: IconName; to: string; bonus: number }[] = [
    {
      id: `action:give:${child.id}`,
      title: `Give credit to ${who}`,
      subtitle: `Move credit down to this ${tier} — opens the member page, nothing moves yet`,
      icon: 'give',
      to: `/member/${encodeURIComponent(child.id)}`,
      bonus: 3,
    },
    {
      id: `action:take:${child.id}`,
      title: `Take credit from ${who}`,
      subtitle: `Pull credit back from this ${tier} — opens the member page, nothing moves yet`,
      icon: 'take',
      to: `/member/${encodeURIComponent(child.id)}`,
      bonus: 2,
    },
    {
      id: `action:statement:${child.id}`,
      title: `Open ${who}'s statement`,
      subtitle: 'Every give and take with this account, with running balances',
      icon: 'transfers',
      to: `/member/${encodeURIComponent(child.id)}?tab=statement`,
      bonus: 1,
    },
    {
      id: `action:bets:${child.id}`,
      title: `Open ${who}'s bets`,
      subtitle: 'The bet log for this account',
      icon: 'exposure',
      to: `/member/${encodeURIComponent(child.id)}?tab=bets`,
      bonus: 0,
    },
  ]

  return rows.map((row) => ({
    id: row.id,
    kind: 'action' as const,
    title: row.title,
    subtitle: row.subtitle,
    ranges: shift(row.title.indexOf(who)),
    to: row.to,
    icon: row.icon,
    // The bonus only orders these four against each other; it is far too small
    // to lift an action above the account row for the same person, which is
    // correct — the person outranks the paperwork about the person.
    score: score + row.bonus,
  }))
}

/**
 * Every action matching `text`, ranked on the shared ladder and never capped.
 *
 * Uncapped because there are at most a dozen and no "see all actions" page to
 * send anyone to; the cap exists to make a 41-row account list honest, not to
 * ration a fixed menu.
 */
export function matchActions(text: string, ctx: ActionContext = {}): SearchHit[] {
  const needle = text.trim()
  if (!needle) return []

  const hits: SearchHit[] = []
  for (const def of ACTIONS) {
    if (def.when && !def.when(ctx)) continue
    const scored = scoreAction(def, needle)
    if (!scored) continue
    hits.push({
      id: def.id,
      kind: 'action',
      title: def.label,
      subtitle: def.subtitle,
      ranges: scored.ranges,
      to: def.to,
      icon: def.icon,
      score: scored.score,
    })
  }

  hits.push(...templatedActions(ctx, needle))
  return hits.sort(compareHits)
}

/**
 * The empty-state "Jump to" list, generated from the nav model for this role.
 *
 * Generated, not hand-listed: a new rail entry becomes searchable with no second
 * edit, which is the only way a duplicated list stays in sync for longer than a
 * quarter. Role filtering mirrors the rail so Jump to never offers a Logs page
 * the operator cannot open.
 */
export function navHits(role?: Role): SearchHit[] {
  return navItemsForRole(role).map((item) => ({
    id: `nav:${item.id}`,
    kind: 'action' as const,
    title: item.label,
    subtitle: item.description,
    ranges: [],
    to: item.to,
    icon: item.icon,
    score: SEARCH_SCORE.base.action,
  }))
}

/** The top actions offered before anything is typed (capped at five). */
export function topActions(ctx: ActionContext = {}): SearchHit[] {
  return ACTIONS.filter((def) => !def.when || def.when(ctx))
    .slice(0, 5)
    .map((def) => ({
      id: def.id,
      kind: 'action' as const,
      title: def.label,
      subtitle: def.subtitle,
      ranges: [],
      to: def.to,
      icon: def.icon,
      score: SEARCH_SCORE.base.action,
    }))
}
